// Copyright © 2019-2026 Focal Systems. All rights reserved.

import type { AuthenticationResponseJSON, RegistrationResponseJSON } from "@simplewebauthn/browser";
import createClient from "openapi-fetch";
import type { components, paths } from "@/api/auth-schema";
import type {
  AuthContextResponse,
  LoginCredentials,
  LoginResponse,
  PasskeyBeginResult,
  PasskeyCredential,
  RealmResponse,
} from "@/lib/auth/types";

// The browser ceremony objects (AuthenticationResponseJSON / RegistrationResponseJSON) are richer
// than the schema's loose `Record<string, never>` for the `credential` body field — cast at the wire.
type CredentialBody = Record<string, never>;

const AUTH_BASE_URL = import.meta.env.VITE_AUTH_URL || import.meta.env.VITE_API_URL;
const SOURCE = "backoffice" as const;

class AuthError extends Error {
  constructor(
    message: string,
    public status: number,
  ) {
    super(message);
  }
}

const authApi = createClient<paths>({ baseUrl: AUTH_BASE_URL });

/** Authorization header from an explicit token, falling back to the stored access token. */
function authHeader(token?: string | null): Record<string, string> {
  const value = token ?? localStorage.getItem("access_token");
  return value ? { Authorization: `Bearer ${value}` } : {};
}

/** openapi-fetch doesn't throw; turn a non-2xx result into an AuthError carrying the status. */
function fail(error: unknown, response: Response): never {
  const body = error as components["schemas"]["ErrorResponse"] | undefined;
  const detail = body?.errors?.[0]?.msg ?? body?.errors?.[0]?.detail;
  throw new AuthError(detail ?? `Request failed: ${response.status}`, response.status);
}

export { AuthError };

export const authClient = {
  async login(credentials: LoginCredentials): Promise<LoginResponse> {
    const { data, error, response } = await authApi.POST("/v1/auth/login", {
      body: { email: credentials.email, password: credentials.password, source: SOURCE },
    });
    if (!data) fail(error, response);
    return data;
  },

  async verifyTwoFactor(mfaCode: string, tempToken: string): Promise<LoginResponse> {
    const { data, error, response } = await authApi.POST("/v1/auth/two-factor", {
      body: { token: mfaCode },
      headers: authHeader(tempToken),
    });
    if (!data) fail(error, response);
    return data;
  },

  async getRealm(email: string): Promise<RealmResponse> {
    const { data, error, response } = await authApi.GET("/v1/auth/realm", {
      params: { query: { email, source: SOURCE } },
    });
    if (!data) fail(error, response);
    return data;
  },

  async claimState(state: string): Promise<LoginResponse> {
    const { data, error, response } = await authApi.POST("/v1/auth/claim-state", {
      params: { query: { state, source: SOURCE } },
    });
    if (!data) fail(error, response);
    return data;
  },

  async getContext(): Promise<AuthContextResponse> {
    const { data, error, response } = await authApi.GET("/v1/auth/context", {
      headers: authHeader(),
    });
    if (!data) fail(error, response);
    return data;
  },

  async logout(): Promise<void> {
    const { error, response } = await authApi.POST("/v1/auth/logout", {
      headers: authHeader(),
    });
    if (!response.ok) fail(error, response);
  },

  /** Change the current user's password (local-realm accounts only — SSO accounts are rejected by the backend).
   * `token` overrides the stored access token — used by the forced-change-at-login flow, where the login
   * response carries a short-lived token (action=changePassword) that authorizes this call before a session
   * exists, mirroring how the two-factor step uses its temp token. */
  async changePassword(oldPassword: string, newPassword: string, token?: string): Promise<void> {
    const { error, response } = await authApi.POST("/v1/auth/password", {
      body: { oldPassword, newPassword },
      headers: authHeader(token),
    });
    if (!response.ok) fail(error, response);
  },

  // --- Passkey login (unauthenticated) ---

  /** Begin a passkey assertion. With an email the challenge is scoped to that user's credentials;
   * without one the browser offers all discoverable passkeys for this RP. */
  async passkeyAuthBegin(email?: string): Promise<PasskeyBeginResult> {
    const { data, error, response } = await authApi.POST("/v1/auth/passkey/authenticate/begin", {
      body: email ? { email } : {},
    });
    if (!data) fail(error, response);
    return data;
  },

  async passkeyAuthComplete(sessionKey: string, credential: AuthenticationResponseJSON): Promise<LoginResponse> {
    const { data, error, response } = await authApi.POST("/v1/auth/passkey/authenticate/complete", {
      body: { sessionKey, credential: credential as unknown as CredentialBody },
    });
    if (!data) fail(error, response);
    return data;
  },

  // --- Passkey registration / management (authenticated) ---

  async passkeyRegisterBegin(): Promise<PasskeyBeginResult> {
    const { data, error, response } = await authApi.POST("/v1/auth/passkey/register/begin", {
      headers: authHeader(),
    });
    if (!data) fail(error, response);
    return data;
  },

  async passkeyRegisterComplete(
    sessionKey: string,
    friendlyName: string,
    credential: RegistrationResponseJSON,
  ): Promise<PasskeyCredential> {
    const { data, error, response } = await authApi.POST("/v1/auth/passkey/register/complete", {
      body: { sessionKey, friendlyName, credential: credential as unknown as CredentialBody },
      headers: authHeader(),
    });
    if (!data) fail(error, response);
    return data;
  },

  async passkeyCredentials(): Promise<PasskeyCredential[]> {
    const { data, error, response } = await authApi.GET("/v1/auth/passkey/credentials", {
      headers: authHeader(),
    });
    if (!data) fail(error, response);
    return data;
  },

  /** Revoke (soft-delete) a passkey by its opaque webauthn id. */
  async passkeyDelete(webauthnId: string): Promise<void> {
    const { error, response } = await authApi.DELETE("/v1/auth/passkey/credentials/{webauthn_id}", {
      params: { path: { webauthn_id: webauthnId } },
      headers: authHeader(),
    });
    if (!response.ok) fail(error, response);
  },
};
